/**
 * =============================================================================
 * SIMILAR QUESTIONS COMPONENT
 * =============================================================================
 *
 * Displays a list of similar questions to help users find existing answers
 * and reduce duplicate submissions.
 *
 * PHASE REFERENCE: PHASE_1_SPEC.md P1-19
 *
 * Features:
 * - Shows title (linked), answer count, and status for each result
 * - Loading state with skeleton animation
 * - Empty state when no similar questions found
 * - Accessible with proper ARIA attributes
 *
 * =============================================================================
 */

import React, { useId, useState } from 'react';
import {
  EDITORIAL_CORRECTION_PRESENTATIONS,
  resolveQuestionStatusPresentation,
} from '@find-my-part/types';
import type { SimilarQuestionItem } from '../api/client';
import { useStyleInjection } from '../hooks';
import { canonicalQuestionHref } from '../lib/questionUrl';

// =============================================================================
// TYPES
// =============================================================================

export interface SimilarQuestionsProps {
  /** Array of similar questions to display */
  questions: SimilarQuestionItem[];
  /** Whether the search is in progress */
  isLoading: boolean;
  /** Whether to show the component (should be true if there's input) */
  show: boolean;
  /** Base URL for question links (e.g., /find-my-part/questions) */
  baseUrl?: string;
  /** Additional CSS class name */
  className?: string;
  /** Selected photos cannot be serialized; preserve their live form in this tab. */
  hasSelectedPhotos?: boolean;
  /** Persist the allow-listed text draft before an ordinary same-tab activation. */
  onBeforeSameTabNavigate?: () => boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format the status for display.
 */
function formatStatus(status: string): { label: string; className: string } {
  const presentation = resolveQuestionStatusPresentation(status);
  return {
    label: presentation.customerLabel,
    className: `fmp-similar__status--${presentation.tone}`,
  };
}

/**
 * Format reply count for display.
 */
function formatReplyCount(count: number): string {
  if (count === 0) return 'No answers yet';
  if (count === 1) return '1 answer';
  return `${count} answers`;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Displays similar questions found during question title input.
 *
 * @example
 * ```tsx
 * const { questions, isLoading } = useSimilarQuestions(title);
 *
 * <SimilarQuestions
 *   questions={questions}
 *   isLoading={isLoading}
 *   show={title.trim().length >= 3}
 * />
 * ```
 */
export function SimilarQuestions({
  questions,
  isLoading,
  show,
  baseUrl = '/find-my-part/questions',
  className = '',
  hasSelectedPhotos = false,
  onBeforeSameTabNavigate,
}: SimilarQuestionsProps): React.ReactElement | null {
  // Generate unique ID for ARIA labelledby (I-C1: accessibility fix for multiple instances)
  const labelId = useId();
  const [persistenceFailed, setPersistenceFailed] = useState(false);
  const opensInNewTab = hasSelectedPhotos || persistenceFailed;

  // Inject styles
  useStyleInjection(similarQuestionsStyles, 'fmp-similar-questions-styles');

  // Don't render if not shown
  if (!show) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={`fmp-similar ${className}`} aria-busy="true" aria-live="polite">
        <p className="fmp-similar__label">Finding similar questions...</p>
        <div className="fmp-similar__skeleton">
          <div className="fmp-similar__skeleton-item fmp-skeleton" />
          <div className="fmp-similar__skeleton-item fmp-skeleton" />
        </div>
      </div>
    );
  }

  // Empty state
  if (questions.length === 0) {
    return (
      <div className={`fmp-similar fmp-similar--empty ${className}`} aria-live="polite">
        <p className="fmp-similar__empty">No similar questions found</p>
      </div>
    );
  }

  // Results
  return (
    <div className={`fmp-similar ${className}`} aria-live="polite">
      <p className="fmp-similar__label" id={labelId}>
        Similar questions ({questions.length}):
      </p>
      <ul
        className="fmp-similar__list"
        role="list"
        aria-labelledby={labelId}
      >
        {questions.map((question) => {
          const { label: statusLabel, className: statusClassName } = formatStatus(question.status);
          const questionUrl = canonicalQuestionHref(question.slug, baseUrl);

          return (
            <li key={question.questionId} className="fmp-similar__item">
              <a
                href={questionUrl}
                className="fmp-similar__link"
                target={opensInNewTab ? '_blank' : undefined}
                rel={opensInNewTab ? 'noopener noreferrer' : undefined}
                onClick={(event) => {
                  if (
                    opensInNewTab
                    || event.button !== 0
                    || event.metaKey
                    || event.ctrlKey
                    || event.shiftKey
                    || event.altKey
                  ) {
                    return;
                  }

                  if (onBeforeSameTabNavigate?.() === false) {
                    event.preventDefault();
                    // The failure is only knowable on activation. Keep the
                    // live form in place, disclose the safe fallback, and let
                    // the shopper explicitly activate the now-new-tab anchor.
                    setPersistenceFailed(true);
                  }
                }}
              >
                <span className="fmp-similar__title">{question.title}</span>
                <span className="fmp-similar__meta">
                  <span className={`fmp-similar__status ${statusClassName}`}>
                    {statusLabel}
                  </span>
                  {question.editoriallyCorrected && (
                    <span>{EDITORIAL_CORRECTION_PRESENTATIONS.CUSTOMER_OR_GUEST}</span>
                  )}
                  <span className="fmp-similar__replies">
                    {formatReplyCount(question.replyCount)}
                  </span>
                  {question.hasSolution && (
                    <span className="fmp-similar__solution" title="Has accepted solution">
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="fmp-similar__check-icon"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
      {hasSelectedPhotos && (
        <p className="fmp-similar__disclosure">
          Opens in a new tab so your selected photos stay here.
        </p>
      )}
      {persistenceFailed && !hasSelectedPhotos && (
        <p className="fmp-similar__disclosure" role="status">
          We couldn&apos;t save this draft. Open a result again in a new tab to keep this
          form here.
        </p>
      )}
      <p className="fmp-similar__hint">
        Check if your question has already been answered before submitting.
      </p>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const similarQuestionsStyles = `
  :where(.fmp-widget-scope) .fmp-similar {
    margin-top: var(--fmp-space-sm);
    padding: var(--fmp-space-md);
    background-color: var(--fmp-color-surface-muted);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
    font-size: var(--fmp-font-size-sm);
  }

  :where(.fmp-widget-scope) .fmp-similar--empty {
    background-color: transparent;
    border-color: var(--fmp-color-border);
  }

  :where(.fmp-widget-scope) .fmp-similar__label {
    margin: 0 0 var(--fmp-space-sm) 0;
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-similar__empty {
    margin: 0;
    color: var(--fmp-color-text-muted);
    font-style: italic;
  }

  :where(.fmp-widget-scope) .fmp-similar__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  :where(.fmp-widget-scope) .fmp-similar__item {
    margin-bottom: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-similar__item:last-child {
    margin-bottom: 0;
  }

  :where(.fmp-widget-scope) .fmp-similar__link {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-xs);
    padding: var(--fmp-space-sm) var(--fmp-size-10);
    background-color: var(--fmp-color-surface);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    text-decoration: none;
    color: inherit;
    transition: box-shadow var(--fmp-duration-fast) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-similar__link:hover {
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-similar__link:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-similar__title {
    color: var(--fmp-color-primary);
    font-weight: var(--fmp-font-weight-semibold);
    line-height: var(--fmp-line-height-heading);
  }

  :where(.fmp-widget-scope) .fmp-similar__link:hover .fmp-similar__title {
    text-decoration: underline;
  }

  :where(.fmp-widget-scope) .fmp-similar__meta {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-similar__status {
    padding: var(--fmp-size-2) var(--fmp-size-6);
    border-radius: var(--fmp-radius-pill);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: var(--fmp-letter-spacing-label);
  }

  :where(.fmp-widget-scope) .fmp-similar__status--neutral {
    background-color: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-similar__status--info {
    background-color: var(--fmp-color-info-bg);
    color: var(--fmp-color-info);
  }

  :where(.fmp-widget-scope) .fmp-similar__status--success {
    background-color: var(--fmp-color-success-bg);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-similar__status--warning {
    background-color: var(--fmp-color-warning-bg);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-similar__status--danger {
    background-color: var(--fmp-color-danger-bg);
    color: var(--fmp-color-danger);
  }

  :where(.fmp-widget-scope) .fmp-similar__replies {
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-similar__solution {
    display: flex;
    align-items: center;
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-similar__check-icon {
    width: var(--fmp-size-14);
    height: var(--fmp-size-14);
  }

  :where(.fmp-widget-scope) .fmp-similar__hint {
    margin: var(--fmp-space-sm) 0 0 0;
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-similar__disclosure {
    margin: var(--fmp-space-sm) 0 0;
    color: var(--fmp-color-text-secondary);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
  }

  /* Canonical .fmp-skeleton owns shimmer, color, radius, and reduced motion. */
  :where(.fmp-widget-scope) .fmp-similar__skeleton {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-similar__skeleton-item {
    height: calc(var(--fmp-size-48) + var(--fmp-size-2));
  }
`;
